import React, { useState } from 'react';
import { IMPACT, PROGRAMS, PROGRAM_LIST } from '@/data/seed';
import { fmtIN, shortIN } from '@/lib/fmt';
import { HEATMAP_CELL, HEATMAP_TOGGLE } from '@/constants/testIds';
import { Users, School, HeartPulse, Sparkles } from 'lucide-react';

const ICONS = {
  students: School, people: HeartPulse, youth: Sparkles, women: Users, centres: School, schools: School,
};

export default function Impact() {
  const [mode, setMode] = useState('beneficiaries');
  const heat = IMPACT.heat;

  // Prepare displayed values based on mode
  const rowsDisplayed = heat.rows.map((r) => {
    if (mode === 'annualised') {
      // YoY growth in absolute-thousands
      return {
        key: r.key,
        vals: r.vals.map((v, i) => (i === 0 ? 0 : v - r.vals[i - 1])),
      };
    }
    return r;
  });
  const flat = rowsDisplayed.flatMap((r) => r.vals);
  const max = Math.max(...flat, 1);
  const color = (v) => {
    const t = Math.max(0, v) / max;
    return `rgba(226, 59, 51, ${0.12 + t * 0.85})`;
  };
  const fmtCell = (v) => {
    if (mode === 'annualised' && v === 0) return '—';
    const prefix = mode === 'annualised' && v > 0 ? '+' : '';
    return prefix + (Math.abs(v) >= 1000 ? (v / 1000).toFixed(1) + 'M' : Math.round(v) + 'K');
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Impact position</div>
          <h1>Beneficiaries &amp; Impact</h1>
          <div className="sub">FY 24-25 actuals — the outcomes we report to donors and the board</div>
        </div>
      </div>

      <div className="seg-grid">
        {IMPACT.segments.map((s) => {
          const Ico = ICONS[s.key] || Users;
          return (
            <div key={s.key} className="seg-card">
              <div className="seg-ring" style={{ borderColor: s.color, color: s.color }}>
                <Ico size={22} strokeWidth={1.8} />
              </div>
              <div className="seg-num">{shortIN(s.val)}</div>
              <div className="seg-label">{s.label}</div>
              <div className="seg-share" title={fmtIN(s.val)}>
                {s.val < 100000 ? fmtIN(s.val) : ''}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Four-year Beneficiary Ledger</div>
            <div className="card-sub">By program pillar × fiscal year · in thousands</div>
          </div>
          <div className="inline-flex border border-[color:var(--gray-200)] rounded-lg overflow-hidden" role="tablist">
            {['beneficiaries', 'annualised'].map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                data-testid={`${HEATMAP_TOGGLE}-${v}`}
                aria-selected={mode === v}
                onClick={() => setMode(v)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold transition-colors ${
                  mode === v ? 'bg-[color:var(--llf-ink)] text-white' : 'text-[color:var(--gray-600)] hover:bg-[color:var(--gray-50)]'
                }`}
              >
                {v === 'beneficiaries' ? 'Total' : 'YoY delta'}
              </button>
            ))}
          </div>
        </div>
        <div className="heat-wrap">
          <table className="heat">
            <thead>
              <tr>
                <th className="rowh"></th>
                {heat.years.map((y) => <th key={y}>{y}</th>)}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {heat.rows.map((row, r) => {
                const total = row.vals.reduce((s, v) => s + v, 0);
                const p = PROGRAMS[row.key];
                return (
                  <tr key={row.key}>
                    <td className="rowl">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                        {p.name}
                      </span>
                    </td>
                    {row.vals.map((v, c) => (
                      <td key={c} data-testid={HEATMAP_CELL(r, c)}
                        style={{ background: color(v), color: '#1F2937' }} title={`${p.name} · ${heat.years[c]} · ${fmtIN(v * 1000)} beneficiaries`}>
                        {v >= 1000 ? (v / 1000).toFixed(1) + 'M' : v + 'K'}
                      </td>
                    ))}
                    <td className="tot">{total >= 1000 ? (total / 1000).toFixed(1) + 'M' : total + 'K'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-3 border-t border-dashed border-[color:var(--gray-200)] flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-[color:var(--gray-500)] font-semibold">Fewer</span>
            {[0.15, 0.35, 0.55, 0.75, 0.95].map((t) => (
              <span key={t} className="h-3 w-6" style={{ background: `rgba(226,59,51,${t})` }} />
            ))}
            <span className="text-[10px] uppercase tracking-wider text-[color:var(--gray-500)] font-semibold">
              More · max {(max / 1000 >= 1 ? (max / 1000).toFixed(1) + 'M' : max + 'K')}
            </span>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-[color:var(--gray-500)]">
            Cumulative · <b className="text-[color:var(--gray-800)]">{shortIN(IMPACT.totalBenef)}</b>
          </div>
        </div>
      </div>

      <div className="sec-title">Pillar delivery — headline numbers</div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {PROGRAM_LIST.map((k) => {
          const p = PROGRAMS[k];
          const pc = IMPACT.programCards[k];
          return (
            <div key={k} className="program-card" style={{ borderTopColor: p.color }}>
              <div className="pc-over" style={{ color: p.color }}>Pillar</div>
              <h3>{p.name}</h3>
              <div className="pc-desc">{p.desc}</div>
              <div className="pc-stats" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {pc.stats.map((s, i) => (
                  <div key={i} className="pc-stat">
                    <div className="v">{s[0]}</div>
                    <div className="l">{s[1]}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
